import {
  signIn as _signIn,
  signUp as _signUp,
  signOut as _signOut,
  sendPasswordReset as _sendPasswordReset,
  updatePassword as _updatePassword,
  updateEmail as _updateEmail,
  getCurrentUser as _getCurrentUser,
  onAuthStateChange as _onAuthStateChange,
  updateProfile as _updateProfile,
} from './auth';
import { debugLog } from './debugLog';
import type { UserProfile } from '../types/forms';

// =====================================================
// AUTH INSTRUMENTADO - Wrappers com logging de diagnóstico
// lib/auth.ts permanece intocado
// =====================================================

export async function signIn(
  email: string,
  password: string,
): Promise<{ user: UserProfile | null; error: string | null }> {
  debugLog('info', 'auth.signIn', `Tentando login: ${email}`);
  const start = performance.now();
  const result = await _signIn(email, password);
  const ms = Math.round(performance.now() - start);

  if (result.error) {
    debugLog('error', 'auth.signIn', `Falhou (${ms}ms): ${result.error}`, { email });
  } else if (result.user) {
    debugLog('success', 'auth.signIn', `Sucesso (${ms}ms)`, {
      userId: result.user.id,
      role: result.user.role,
    });
  } else {
    debugLog('warn', 'auth.signIn', `Retornou sem erro mas sem user (${ms}ms)`, { email });
  }
  return result;
}

export async function signUp(
  email: string,
  password: string,
  fullName?: string,
): Promise<{ user: UserProfile | null; error: string | null }> {
  debugLog('info', 'auth.signUp', `Tentando cadastro: ${email}`);
  const start = performance.now();
  const result = await _signUp(email, password, fullName);
  const ms = Math.round(performance.now() - start);

  if (result.error) {
    debugLog('error', 'auth.signUp', `Falhou (${ms}ms): ${result.error}`, { email });
  } else {
    debugLog('success', 'auth.signUp', `Sucesso (${ms}ms) — verificar email`, { email });
  }
  return result;
}

export async function signOut(): Promise<{ error: string | null }> {
  debugLog('info', 'auth.signOut', 'Saindo...');
  const result = await _signOut();

  if (result.error) {
    debugLog('error', 'auth.signOut', `Falhou: ${result.error}`);
  } else {
    debugLog('success', 'auth.signOut', 'Logout realizado');
  }
  return result;
}

export async function getCurrentUser(): Promise<UserProfile | null> {
  debugLog('info', 'auth.getCurrentUser', 'Buscando usuário da sessão...');
  const start = performance.now();
  const user = await _getCurrentUser();
  const ms = Math.round(performance.now() - start);

  if (user) {
    debugLog('success', 'auth.getCurrentUser', `Usuário encontrado (${ms}ms)`, {
      userId: user.id,
      email: user.email,
      role: user.role,
    });
  } else {
    debugLog('warn', 'auth.getCurrentUser', `Sem sessão ativa (${ms}ms)`);
  }
  return user;
}

export function onAuthStateChange(callback: (user: UserProfile | null) => void) {
  debugLog('info', 'auth.onAuthStateChange', 'Listener de auth registrado');

  return _onAuthStateChange((user) => {
    if (user) {
      debugLog('info', 'auth.onAuthStateChange', 'Estado mudou: usuário presente', {
        userId: user.id,
        email: user.email,
        role: user.role,
      });
    } else {
      debugLog('warn', 'auth.onAuthStateChange', 'Estado mudou: sem usuário (logout ou expirado)');
    }
    callback(user);
  });
}

export async function sendPasswordReset(email: string) {
  debugLog('info', 'auth.sendPasswordReset', `Enviando reset para ${email}`);
  const result = await _sendPasswordReset(email);

  if (result.error) {
    debugLog('error', 'auth.sendPasswordReset', result.error);
  } else {
    debugLog('success', 'auth.sendPasswordReset', 'Email de reset enviado');
  }
  return result;
}

export async function updatePassword(newPassword: string) {
  debugLog('info', 'auth.updatePassword', 'Atualizando senha...');
  const result = await _updatePassword(newPassword);

  if (result.error) {
    debugLog('error', 'auth.updatePassword', result.error);
  } else {
    debugLog('success', 'auth.updatePassword', 'Senha atualizada');
  }
  return result;
}

export async function updateEmail(newEmail: string) {
  debugLog('info', 'auth.updateEmail', `Atualizando email para ${newEmail}`);
  const result = await _updateEmail(newEmail);

  if (result.error) {
    debugLog('error', 'auth.updateEmail', result.error);
  } else {
    debugLog('success', 'auth.updateEmail', 'Email atualizado');
  }
  return result;
}

export async function updateProfile(
  userId: string,
  updates: Partial<Pick<UserProfile, 'full_name' | 'avatar_url' | 'username'>>,
) {
  debugLog('info', 'auth.updateProfile', `Atualizando perfil ${userId}`, updates);
  const result = await _updateProfile(userId, updates);

  if (result.error) {
    debugLog('error', 'auth.updateProfile', result.error);
  } else {
    debugLog('success', 'auth.updateProfile', 'Perfil atualizado');
  }
  return result;
}
